import React, { useContext, useEffect, useState } from "react";
import { Container, Nav, Tab, Spinner, Alert, Table, Button, Form, Badge, Row, Col } from "react-bootstrap";
import DataTable from "../components/DataTable";
import Select from "react-select";
import { FaPen, FaSearch, FaFilePdf, FaInfoCircle, FaUsers, FaCalendarAlt, FaCalendarDay, FaUser, FaSyncAlt, FaDownload, FaLayerGroup } from "react-icons/fa";
import "../components/salaire/SalairesTabShared.css";
import { RsKpiCard, RsKpiGrid, RsEmptyHint, RsLoadingBlock, RsSectionCard } from "../components/salaire/SalairesUi";
import ValidationSalaire from "../components/salaire/ValidationSalaire";
import RechercheSalaire from "../components/salaire/RechercheSalaire";
import Navbar from "../components/Navbar";
import AppBreadcrumb from "../components/AppBreadcrumb";
import Sidebar from "../components/Sidebar";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "../context/AxiosInstance";
import { toast } from "react-toastify";
import RhTablePagination from "../components/common/RhTablePagination";
import "../styles/salaires-page.css";

const MOIS_OPTIONS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const buildAnneesOptions = () => {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1];
};

const formatGnf = (n) => `${Number(n || 0).toLocaleString("fr-FR")} GNF`;

const buildSalaireParContrat = (salaires, crgMeta) => {
  const crgById = new Map((crgMeta || []).map((c) => [c.id_crg, c]));
  const agg = new Map();
  for (const s of salaires || []) {
    const id = s.id_contrat;
    if (!agg.has(id)) agg.set(id, { gains: 0, charge_salariale: 0 });
    const row = agg.get(id);
    const montantGain = s.montant_gain_salaire || 0;
    const montantRetenu = s.montant_retenu_salaire || 0;
    row.gains += montantGain;
    const meta = crgById.get(s.id_crg);
    const prise = String(meta?.prise_en_charge_crg || "").toLowerCase();
    if (prise === "salariale") row.charge_salariale += montantRetenu;
  }
  const result = new Map();
  for (const [id, r] of agg) {
    result.set(id, {
      hasSalaire: true,
      net: r.gains - r.charge_salariale,
      brut: r.gains,
    });
  }
  return result;
};

const moisOptionsAll = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

// Tab: Recherche salaire multiple (inséré)
const RechercheMultipleInsereTab = () => {
  const [mois, setMois] = useState("");
  const [annee, setAnnee] = useState("");
  const [employe, setEmploye] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [rows, setRows] = useState([]);
  const [kpi, setKpi] = useState({ brut: 0, net: 0, charge_salariale: 0, charge_patronale: 0, gains: 0, retenues: 0 });
  const [contrats, setContrats] = useState([]);
  const [crgMeta, setCrgMeta] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [resContrats, resCrg] = await Promise.all([
          axios.get('/contrats/'),
          axios.get('/crg/')
        ]);
        setContrats(Array.isArray(resContrats.data?.data) ? resContrats.data.data : []);
        setCrgMeta(Array.isArray(resCrg.data?.data) ? resCrg.data.data : []);
      } catch {
        setContrats([]);
        setCrgMeta([]);
      }
    };
    loadData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1) Fetch all salaires for filters
      const params = { annee: annee || undefined, mois: mois || undefined, is_active: 1 };
      const res = await axios.get("/salaires/", { params });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];

      // 2) Optionnel: filter by employee via contract
      let filtered = data;
      if (employe) {
        const contratsIds = (contrats || []).filter(c => c.id_employe === employe).map(c => c.id_contrat);
        filtered = data.filter(s => contratsIds.includes(s.id_contrat));
      }

      // 3) Build aggregates per id_employe
      const contratById = new Map();
      (contrats || []).forEach(c => contratById.set(c.id_contrat, c));
      const crgById = new Map();
      (crgMeta || []).forEach(crg => crgById.set(crg.id_crg, crg));

      const aggByEmp = new Map();
      for (const s of filtered) {
        const c = contratById.get(s.id_contrat);
        const idEmp = c?.id_employe || 0;
        if (!idEmp) continue;
        if (!aggByEmp.has(idEmp)) {
          aggByEmp.set(idEmp, {
            id_employe: idEmp,
            nom: c?.nom_employe || "",
            prenom: c?.prenom_employe || "",
            poste: c?.poste || "",
            id_contrat: s.id_contrat,
            brut: 0,
            gains: 0,
            retenues: 0,
            charge_salariale: 0,
            charge_patronale: 0,
            net: 0,
            moisPeriodes: new Set(),
          });
        }
        const row = aggByEmp.get(idEmp);
        const moisS = s.mois_salaire || s.mois;
        const anneeS = s.annee_salaire ?? s.annee;
        if (moisS && anneeS) {
          row.moisPeriodes.add(`${moisS}|${anneeS}`);
        }
        const montant_gain = s.montant_gain_salaire || 0;
        const montant_retenu = s.montant_retenu_salaire || 0;
        row.gains += montant_gain;
        row.retenues += montant_retenu;
        // Charges: 
        // - Salariale: somme des retenues avec prise_en_charge_crg == 'Salariale'
        // - Patronale: somme des retenues avec prise_en_charge_crg == 'Patronale' ET type_crg contient '3_Cotisation'
        const meta = crgById.get(s.id_crg);
        const prise = String(meta?.prise_en_charge_crg || '').toLowerCase();
        const typeCrg = String(meta?.type_crg || '');
        if (prise === 'salariale') {
          row.charge_salariale += montant_retenu;
        } else if (prise === 'patronale' && (/^3[_\-\s]?/i.test(typeCrg) || /cotisation/i.test(typeCrg))) {
          row.charge_patronale += montant_retenu;
        }
      }

      // Finalize: 
      // Brut = gains
      // Net = gains - charge_salariale
      const out = Array.from(aggByEmp.values()).map((r) => {
        r.brut = r.gains;
        r.net = r.gains - r.charge_salariale;
        r.nombre_mois_inseres = r.moisPeriodes?.size ?? 0;
        delete r.moisPeriodes;
        return r;
      });

      // Compute KPI totals
      const k = out.reduce((acc, r) => ({
        brut: acc.brut + (r.brut || 0),
        gains: acc.gains + (r.gains || 0),
        retenues: acc.retenues + (r.retenues || 0),
        net: acc.net + (r.net || 0),
        charge_salariale: acc.charge_salariale + (r.charge_salariale || 0),
        charge_patronale: acc.charge_patronale + (r.charge_patronale || 0),
      }), { brut: 0, gains: 0, retenues: 0, net: 0, charge_salariale: 0, charge_patronale: 0 });

      setRows(out);
      setKpi(k);
      setHasSearched(true);
    } finally { setLoading(false); }
  };

  // Missing salary list for contracts in a month
  const [missing, setMissing] = useState([]);
  const fetchMissing = async () => {
    if (!mois || !annee) { setMissing([]); return; }
    // Contracts with no salaire for selected month/year
    try {
      const [resContrats, resSalaires] = await Promise.all([
        axios.get('/contrats/'),
        axios.get('/salaires/', { params: { mois, annee, is_active: 1 } })
      ]);
      const cs = Array.isArray(resContrats.data?.data) ? resContrats.data.data : [];
      const ss = Array.isArray(resSalaires.data?.data) ? resSalaires.data.data : [];
      const hasSalaireContrat = new Set(ss.map(s => s.id_contrat));
      const miss = cs.filter(c => !hasSalaireContrat.has(c.id_contrat));
      setMissing(miss);
    } catch { setMissing([]); }
  };

  useEffect(() => { fetchMissing(); /* eslint-disable-next-line */ }, [mois, annee]);

  // Options employés (dédupliquées par id_employe) pour le champ recherchable
  const employeOptions = Array.from(new Map((contrats || []).map((c) => [c.id_employe, c])).values())
    .map((c) => ({ value: c.id_employe, label: `${c.nom_employe} ${c.prenom_employe} | ${c.poste || "—"}` }));

  return (
    <div className="salaires-tab-unified">
      <div className="salaires-toolbar">
        <div className="salaires-toolbar-header">
          <h4 className="salaires-toolbar-title">
            <FaUsers /> Recherche salaire multiple (inséré)
          </h4>
          <span className="text-muted small">Résultats : {rows.length}</span>
        </div>
        <Row className="g-3 salaires-filters-row align-items-end">
          <Col md={3}>
            <Form.Label><FaCalendarDay /> Mois</Form.Label>
            <Form.Select value={mois} onChange={(e) => setMois(e.target.value)}>
              <option value="">Tous</option>
              {moisOptionsAll.map((m) => (<option key={m} value={m}>{m}</option>))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label><FaCalendarAlt /> Année</Form.Label>
            <Form.Select value={annee} onChange={(e) => setAnnee(e.target.value)}>
              <option value="">—</option>
              {[2023, 2024, 2025, 2026].map((y) => (<option key={y} value={y}>{y}</option>))}
            </Form.Select>
          </Col>
          <Col md={5}>
            <Form.Label><FaUser /> Employé</Form.Label>
            <Select
              isClearable
              placeholder="Rechercher un employé…"
              noOptionsMessage={() => "Aucun employé"}
              options={employeOptions}
              value={employeOptions.find((o) => o.value === employe) || null}
              onChange={(opt) => setEmploye(opt ? opt.value : "")}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </Col>
          <Col md={2} className="rs-btn-col">
            <Button className="btn-rs-search w-100" onClick={fetchData}>
              <FaSearch /> Rechercher
            </Button>
          </Col>
        </Row>
      </div>

      {!hasSearched && (
        <RsEmptyHint title="Comment commencer">
          Choisissez au minimum un mois et une année, puis cliquez sur « Rechercher » pour afficher les agrégats et tableaux.
        </RsEmptyHint>
      )}

      {hasSearched && loading && <RsLoadingBlock label="Chargement des salaires…" />}

      {hasSearched && !loading && (
        <>
          <RsKpiGrid>
            <RsKpiCard label="Salaire brut" value={kpi.brut} variant="brut" />
            <RsKpiCard label="Salaire net" value={kpi.net} variant="net" />
            <RsKpiCard label="Charge salariale" value={kpi.charge_salariale} variant="charge-sal" />
            <RsKpiCard label="Charge patronale" value={kpi.charge_patronale} variant="charge-pat" />
          </RsKpiGrid>

          <RsSectionCard
            title="Employés avec contrat et salaire"
            badge={<Badge className="rs-count-badge">{rows.length}</Badge>}
          >
            <p className="rs-section-hint">
              Résumé des salaires existants pour la période sélectionnée. La colonne « Mois insérés » indique le nombre de mois distincts avec au moins un enregistrement de salaire pour chaque employé.
            </p>
            <DataTable
              columns={[
                { label: "Employé", render: (r) => `${r.nom} ${r.prenom}` },
                {
                  label: "Mois insérés",
                  render: (r) => (
                    <Badge bg={r.nombre_mois_inseres > 0 ? "primary" : "secondary"} className="rs-count-badge">
                      {r.nombre_mois_inseres ?? 0} mois
                    </Badge>
                  ),
                },
                { label: "Brut", render: (r) => `${(r.brut || 0).toLocaleString()} GNF` },
                { label: "Charge salariale", render: (r) => `${(r.charge_salariale || 0).toLocaleString()} GNF` },
                { label: "Charge patronale", render: (r) => `${(r.charge_patronale || 0).toLocaleString()} GNF` },
                { label: "Net", render: (r) => <span className="col-gain fw-semibold">{(r.net || 0).toLocaleString()} GNF</span> },
              ]}
              data={rows}
              emptyText="Aucun salaire trouvé pour ces filtres"
              pageSize={10}
            />
          </RsSectionCard>

          <RsSectionCard
            title="Employés avec contrat mais sans salaire"
            badge={<Badge bg="secondary">{missing.length}</Badge>}
          >
            <p className="rs-section-hint">
              Contrats actifs sans salaire enregistré pour la période choisie.
            </p>
            <Row className="g-2 mb-3 align-items-end salaires-filters-row">
              <Col md={4}>
                <Form.Label><FaCalendarDay /> Mois</Form.Label>
                <Form.Select value={mois} onChange={(e) => setMois(e.target.value)}>
                  <option value="">— Mois —</option>
                  {moisOptionsAll.map((m) => (<option key={m} value={m}>{m}</option>))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label><FaCalendarAlt /> Année</Form.Label>
                <Form.Select value={annee} onChange={(e) => setAnnee(e.target.value)}>
                  <option value="">— Année —</option>
                  {[2023, 2024, 2025, 2026].map((y) => (<option key={y} value={y}>{y}</option>))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Button className="btn-rs-outline w-100" onClick={fetchMissing}>Afficher</Button>
              </Col>
            </Row>
            <DataTable
              columns={[
                { label: "Employé", render: (c) => `${c.nom_employe} ${c.prenom_employe}` },
                { label: "Poste", render: (c) => c.poste || "—" },
                { label: "Contrat", render: (c) => `#${c.id_contrat}` },
              ]}
              data={missing}
              emptyText="Aucun employé sans salaire pour la période"
              pageSize={10}
            />
          </RsSectionCard>
        </>
      )}
    </div>
  );
};

const BulletinsGeneresTab = () => {
  const { role } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filtreMois, setFiltreMois] = useState("");
  const [filtreAnnee, setFiltreAnnee] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(20);

  const fetchList = async () => {
    if (role !== "RH" && role !== "ADMIN") return;
    setLoading(true);
    try {
      const res = await axios.get("/bulletins/admin/list", { params: { page, per_page: perPage, search, mois: filtreMois || undefined, annee: filtreAnnee || undefined } });
      setItems(res.data?.data || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  // Live search: recharge après une courte pause de frappe
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchList();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filtreMois, filtreAnnee]);

  return (
    <div className="salaires-tab-unified">
      <div className="salaires-toolbar">
        <div className="salaires-toolbar-header">
          <h4 className="salaires-toolbar-title">
            <FaFilePdf /> Bulletins générés
          </h4>
          <Button className="btn-rs-refresh" onClick={() => fetchList()}>
            <FaSyncAlt /> Actualiser
          </Button>
        </div>
        <Row className="g-3 salaires-filters-row align-items-end">
          <Col md={2}>
            <Form.Label><FaCalendarDay /> Mois</Form.Label>
            <Form.Select value={filtreMois} onChange={(e) => { setFiltreMois(e.target.value); setPage(1); }}>
              <option value="">Tous</option>
              {["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label><FaCalendarAlt /> Année</Form.Label>
            <Form.Select value={filtreAnnee} onChange={(e) => { setFiltreAnnee(e.target.value); setPage(1); }}>
              <option value="">Toutes</option>
              {[2023, 2024, 2025, 2026].map((y) => (<option key={y} value={y}>{y}</option>))}
            </Form.Select>
          </Col>
          <Col md={5}>
            <Form.Label><FaSearch /> Recherche</Form.Label>
            <Form.Control
              placeholder="Nom, prénom, matricule, mois…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col md={2} className="rs-btn-col">
            <Button className="btn-rs-search w-100" onClick={() => { setPage(1); fetchList(); }}>
              Rechercher
            </Button>
          </Col>
        </Row>
      </div>

      <div className="salaires-table-card">
        <div className="table-section-header d-flex justify-content-between align-items-center">
          <h6>Liste des bulletins</h6>
          <Badge className="rs-count-badge">{total} résultat(s)</Badge>
        </div>
        {loading ? (
          <RsLoadingBlock label="Chargement des bulletins…" />
        ) : items.length === 0 ? (
          <div className="salaires-empty-hint m-3">
            <strong>Aucun bulletin</strong>
            Aucun bulletin ne correspond à vos critères de recherche.
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="mb-0 rs-table-modern">
                <thead>
                  <tr>
                    <th>Employé</th>
                    <th>Matricule</th>
                    <th>Département</th>
                    <th>Période</th>
                    <th>Date génération</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((b) => (
                    <tr key={b.id_bulletin}>
                      <td className="col-crg">{b.employe?.nom} {b.employe?.prenom}</td>
                      <td>{b.employe?.matricule}</td>
                      <td>{b.employe?.departement || "—"}</td>
                      <td><Badge bg="secondary">{b.mois} {b.annee}</Badge></td>
                      <td>{b.date_generation}</td>
                      <td>
                        <Button
                          size="sm"
                          className="btn-rs-outline"
                          onClick={async () => {
                            try {
                              const resp = await axios.get(`/bulletins/admin/telecharger/${b.id_bulletin}`, { responseType: "blob" });
                              const url = window.URL.createObjectURL(new Blob([resp.data]));
                              const link = document.createElement("a");
                              link.href = url;
                              link.setAttribute("download", b.nom_fichier || `bulletin_${b.mois}_${b.annee}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (e) {}
                          }}
                        >
                          <FaDownload /> Télécharger
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <RhTablePagination
              currentPage={page}
              totalItems={total}
              perPage={perPage}
              onPageChange={setPage}
              onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
              itemLabel="bulletins"
              serverMode
            />
          </>
        )}
      </div>
    </div>
  );
};

const GenerationMultipleTab = () => {
  const { role } = useContext(AuthContext);
  const [contrats, setContrats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingSalaires, setLoadingSalaires] = useState(false);
  const [salaireParContrat, setSalaireParContrat] = useState(new Map());
  const [selectedForGeneration, setSelectedForGeneration] = useState([]);
  const [mois, setMois] = useState("");
  const [annee, setAnnee] = useState("");
  const [running, setRunning] = useState(false);
  const [statuses, setStatuses] = useState({});
  const [selectedContrats, setSelectedContrats] = useState([]);
  const [selectedPostes, setSelectedPostes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const annees = buildAnneesOptions();
  const periodeChoisie = Boolean(mois && annee);

  const fetchContrats = async () => {
    if (role !== "RH") return;
    setLoading(true);
    try {
      const res = await axios.get("/contrats/");
      setContrats(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      setContrats([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSalairesPeriode = async () => {
    if (!mois || !annee) {
      setSalaireParContrat(new Map());
      return;
    }
    setLoadingSalaires(true);
    try {
      const [resSalaires, resCrg] = await Promise.all([
        axios.get("/salaires/", { params: { mois, annee: Number(annee), is_active: 1 } }),
        axios.get("/crg/"),
      ]);
      const salaires = Array.isArray(resSalaires.data?.data) ? resSalaires.data.data : [];
      const crgMeta = Array.isArray(resCrg.data?.data) ? resCrg.data.data : [];
      setSalaireParContrat(buildSalaireParContrat(salaires, crgMeta));
    } catch {
      setSalaireParContrat(new Map());
      toast.error("Impossible de charger les salaires pour cette période.");
    } finally {
      setLoadingSalaires(false);
    }
  };

  useEffect(() => { fetchContrats(); }, []);

  useEffect(() => {
    fetchSalairesPeriode();
    setSelectedContrats([]);
    setSelectedPostes([]);
    setSelectAll(false);
    setSelectedForGeneration([]);
    setStatuses({});
  }, [mois, annee]);

  const hasSalaire = (idContrat) => salaireParContrat.has(idContrat);
  const getSalaireInfo = (idContrat) => salaireParContrat.get(idContrat);

  const postesUniques = [...new Set(contrats.map((c) => c.poste).filter(Boolean))];

  const contratsFiltres = contrats.filter((c) => {
    const searchLower = searchTerm.toLowerCase();
    const nomComplet = `${c.nom_employe || ""} ${c.prenom_employe || ""}`.toLowerCase();
    const poste = (c.poste || "").toLowerCase();
    const matchSearch = nomComplet.includes(searchLower) || poste.includes(searchLower);
    // Les postes sélectionnés filtrent la liste affichée
    const matchPoste = selectedPostes.length === 0 || selectedPostes.includes(c.poste);
    return matchSearch && matchPoste;
  });

  const contratsAvecSalaireFiltres = contratsFiltres.filter((c) => hasSalaire(c.id_contrat));
  const statsPeriode = {
    total: contrats.length,
    avecSalaire: contrats.filter((c) => hasSalaire(c.id_contrat)).length,
    sansSalaire: contrats.filter((c) => !hasSalaire(c.id_contrat)).length,
  };

  const handleSelectAll = () => {
    if (!periodeChoisie) {
      toast.warning("Sélectionnez d'abord le mois et l'année de génération.");
      return;
    }
    if (selectAll) {
      setSelectedContrats([]);
      setSelectAll(false);
    } else {
      const ids = contratsAvecSalaireFiltres.map((c) => c.id_contrat);
      setSelectedContrats(ids);
      setSelectAll(ids.length > 0);
      if (ids.length === 0) {
        toast.info("Aucun employé avec salaire sur cette période dans la liste affichée.");
      }
    }
  };

  const handleSelectContrat = (contrat) => {
    if (!periodeChoisie) {
      toast.warning("Sélectionnez d'abord le mois et l'année de génération.");
      return;
    }
    if (!hasSalaire(contrat.id_contrat)) {
      toast.warning(
        `${contrat.nom_employe || ""} ${contrat.prenom_employe || ""} n'a pas de salaire pour ${mois} ${annee} — sélection impossible.`
      );
      return;
    }
    if (selectedContrats.includes(contrat.id_contrat)) {
      setSelectedContrats((prev) => prev.filter((id) => id !== contrat.id_contrat));
      setSelectAll(false);
    } else {
      setSelectedContrats((prev) => [...prev, contrat.id_contrat]);
    }
  };

  const handleSelectPoste = (poste) => {
    if (!periodeChoisie) {
      toast.warning("Sélectionnez d'abord le mois et l'année de génération.");
      return;
    }
    if (selectedPostes.includes(poste)) {
      setSelectedPostes((prev) => prev.filter((p) => p !== poste));
    } else {
      setSelectedPostes((prev) => [...prev, poste]);
    }
  };

  // Seuls les employés réellement cochés (cases / "Tous") comptent pour la file.
  // Les postes servent uniquement de filtre d'affichage.
  const contratsSelectionnes = contrats.filter(
    (c) => selectedContrats.includes(c.id_contrat) && hasSalaire(c.id_contrat)
  );

  const addToGeneration = () => {
    if (!periodeChoisie) {
      toast.warning("Sélectionnez le mois et l'année avant d'ajouter des employés.");
      return;
    }
    const nouveauxContrats = contratsSelectionnes.map((c) => {
      const info = getSalaireInfo(c.id_contrat);
      return {
        value: c.id_contrat,
        label: `${c.nom_employe || ""} ${c.prenom_employe || ""} | ${c.poste || "—"}`,
        net: info?.net ?? 0,
      };
    });
    const existants = new Set(selectedForGeneration.map((c) => c.value));
    const uniques = nouveauxContrats.filter((c) => !existants.has(c.value));
    if (uniques.length === 0) {
      toast.info("Aucun nouvel employé à ajouter (déjà dans la file ou sans salaire).");
      return;
    }
    setSelectedForGeneration((prev) => [...prev, ...uniques]);
    toast.success(`${uniques.length} employé(s) ajouté(s) à la file de génération.`);
  };

  const removeFromGeneration = (idContrat) => {
    setSelectedForGeneration((prev) => prev.filter((c) => c.value !== idContrat));
  };

  const resetAll = () => {
    setSelectedForGeneration([]);
    setMois("");
    setAnnee("");
    setStatuses({});
    setSelectedContrats([]);
    setSelectedPostes([]);
    setSelectAll(false);
    setSearchTerm("");
  };

  const canStart = selectedForGeneration.length > 0 && periodeChoisie && role === "RH" && !running;

  const runSequential = async () => {
    setRunning(true);
    const newStatuses = {};
    setStatuses({});
    for (const opt of selectedForGeneration) {
      newStatuses[opt.value] = "pending";
      setStatuses({ ...newStatuses });
      try {
        await axios.post("/salaires/verrouiller/", null, {
          params: { id_contrat: opt.value, mois, annee: Number(annee) },
        });
        newStatuses[opt.value] = "ok";
        setStatuses({ ...newStatuses });
      } catch (e) {
        const msg = e?.response?.data?.detail || "";
        if (typeof msg === "string" && msg.toLowerCase().includes("déjà verrouill")) {
          newStatuses[opt.value] = "exists";
        } else {
          newStatuses[opt.value] = "error";
        }
        setStatuses({ ...newStatuses });
      }
    }
    setRunning(false);
    toast.success("Génération multiple terminée.");
  };

  const renderSalaireBadge = (c) => {
    if (!periodeChoisie) {
      return <span className="gen-salaire-badge gen-salaire-badge--idle">Choisir une période</span>;
    }
    if (loadingSalaires) {
      return <span className="gen-salaire-badge gen-salaire-badge--loading">Chargement…</span>;
    }
    const info = getSalaireInfo(c.id_contrat);
    if (info) {
      return (
        <span className="gen-salaire-badge gen-salaire-badge--ok" title="Salaire net de la période">
          <span className="gen-salaire-dot" aria-hidden />
          Net {formatGnf(info.net)}
        </span>
      );
    }
    return (
      <span className="gen-salaire-badge gen-salaire-badge--none">
        <span className="gen-salaire-dot" aria-hidden />
        Aucun salaire ce mois
      </span>
    );
  };

  return (
    <div className="salaires-tab-unified generation-multiple-wrap">
      <div className="gen-hero salaires-toolbar">
        <div>
          <h5 className="salaires-toolbar-title mb-1"><FaLayerGroup /> Génération multiple de bulletins</h5>
          <p className="gen-hero-sub mb-0">
            Verrouillez et générez les bulletins pour plusieurs employés en une seule opération.
          </p>
        </div>
        <ol className="gen-steps" aria-label="Étapes">
          <li className={periodeChoisie ? "done" : "active"}><span>1</span> Période</li>
          <li className={periodeChoisie ? "active" : ""}><span>2</span> Sélection</li>
          <li><span>3</span> Génération</li>
        </ol>
      </div>

      <div className="salaires-section-card gen-period-card">
        <div className="section-header">
          <strong>Période de génération</strong>
          {periodeChoisie && (
            <Badge bg="light" text="dark" className="gen-period-pill">
              {mois} {annee}
            </Badge>
          )}
        </div>
        <div className="section-body">
          <Row className="g-3 align-items-end salaires-filters-row">
            <Col md={4} lg={3}>
              <Form.Label><FaCalendarDay /> Mois</Form.Label>
              <Form.Select value={mois} onChange={(e) => setMois(e.target.value)} disabled={running}>
                <option value="">Sélectionner un mois</option>
                {MOIS_OPTIONS.map((m) => (<option key={m} value={m}>{m}</option>))}
              </Form.Select>
            </Col>
            <Col md={3} lg={2}>
              <Form.Label><FaCalendarAlt /> Année</Form.Label>
              <Form.Select value={annee} onChange={(e) => setAnnee(e.target.value)} disabled={running}>
                <option value="">Sélectionner une année</option>
                {annees.map((y) => (<option key={y} value={y}>{y}</option>))}
              </Form.Select>
            </Col>
            <Col md={5} lg={7}>
              {periodeChoisie && (
                <div className="gen-kpi-row">
                  <div className="gen-kpi">
                    <span className="gen-kpi-val">{statsPeriode.total}</span>
                    <span className="gen-kpi-lbl">Contrats</span>
                  </div>
                  <div className="gen-kpi gen-kpi--ok">
                    <span className="gen-kpi-val">{statsPeriode.avecSalaire}</span>
                    <span className="gen-kpi-lbl">Avec salaire</span>
                  </div>
                  <div className="gen-kpi gen-kpi--warn">
                    <span className="gen-kpi-val">{statsPeriode.sansSalaire}</span>
                    <span className="gen-kpi-lbl">Sans salaire</span>
                  </div>
                </div>
              )}
              {!periodeChoisie && (
                <Alert variant="light" className="mb-0 gen-period-hint">
                  Choisissez le mois et l&apos;année pour afficher le statut salaire de chaque employé.
                </Alert>
              )}
            </Col>
          </Row>
        </div>
      </div>

      <div className="salaires-section-card">
        <div className="section-header">
          <strong>Sélection des employés</strong>
          {loadingSalaires && <Spinner animation="border" size="sm" />}
        </div>
        <div className={`section-body ${!periodeChoisie ? "gen-panel-disabled" : ""}`}>
          <Row className="mb-3 g-3">
            <Col md={6}>
              <Form.Label>Rechercher</Form.Label>
              <Form.Control
                type="search"
                placeholder="Nom, prénom ou poste…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={!periodeChoisie || loading}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Sélection par poste</Form.Label>
              <div className="gen-poste-chips">
                {postesUniques.length === 0 ? (
                  <span className="text-muted small">Aucun poste</span>
                ) : (
                  postesUniques.map((poste) => (
                    <button
                      key={poste}
                      type="button"
                      className={`gen-poste-chip ${selectedPostes.includes(poste) ? "active" : ""}`}
                      onClick={() => handleSelectPoste(poste)}
                      disabled={!periodeChoisie || running}
                    >
                      {poste}
                    </button>
                  ))
                )}
              </div>
            </Col>
          </Row>

          {!periodeChoisie ? (
            <RsEmptyHint title="Étape 1 — Période">
              Choisissez le mois et l&apos;année ci-dessus pour afficher la liste des employés et leur statut salaire.
            </RsEmptyHint>
          ) : loading ? (
            <RsLoadingBlock label="Chargement des contrats…" />
          ) : (
            <div className="salaires-table-card">
              <div className="table-section-header">
                <h6>Liste des employés ({contratsFiltres.length})</h6>
              </div>
              <DataTable
                columns={[
                  {
                    label: (
                      <Form.Check
                        type="checkbox"
                        checked={selectAll && contratsAvecSalaireFiltres.length > 0}
                        onChange={handleSelectAll}
                        disabled={!periodeChoisie || contratsAvecSalaireFiltres.length === 0}
                        label="Tous"
                      />
                    ),
                    render: (c) => (
                      <Form.Check
                        type="checkbox"
                        checked={selectedContrats.includes(c.id_contrat)}
                        onChange={() => handleSelectContrat(c)}
                        className={!hasSalaire(c.id_contrat) && periodeChoisie ? "gen-check-muted" : ""}
                        aria-label={`Sélectionner ${c.nom_employe} ${c.prenom_employe}`}
                      />
                    ),
                  },
                  {
                    label: "Employé",
                    render: (c) => (
                      <div className="gen-emp-cell">
                        <span className="gen-emp-name">{c.nom_employe} {c.prenom_employe}</span>
                        <span className="gen-emp-meta">Contrat #{c.id_contrat}</span>
                      </div>
                    ),
                  },
                  { label: "Poste", render: (c) => c.poste || "—" },
                  { label: "Département", render: (c) => c.departement || "—" },
                  { label: "Salaire (période)", render: (c) => renderSalaireBadge(c) },
                ]}
                data={contratsFiltres}
                emptyText="Aucun employé trouvé"
                pageSize={25}
                pageSizeOptions={[10, 25, 50, 100]}
                rowClassName={(c) => (!periodeChoisie || hasSalaire(c.id_contrat) ? "" : "gen-row-no-salaire")}
              />
            </div>
          )}

          <div className="gen-actions-bar">
            <div className="gen-badges">
              <Badge bg="primary" className="gen-count-badge">
                {contratsSelectionnes.length} sélectionné(s)
              </Badge>
              <Badge bg="secondary" className="gen-count-badge">
                {selectedPostes.length} poste(s) filtré(s)
              </Badge>
            </div>
            <Button
              className="gen-btn-add"
              variant="primary"
              onClick={addToGeneration}
              disabled={!periodeChoisie || contratsSelectionnes.length === 0 || running}
            >
              Ajouter à la file ({contratsSelectionnes.length})
            </Button>
          </div>
        </div>
      </div>

      <div className="salaires-section-card gen-queue-card">
        <div className="section-header">
          <strong>File de génération</strong>
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline-secondary" size="sm" onClick={resetAll} disabled={running}>
              Réinitialiser
            </Button>
            <Button
              className="gen-btn-run"
              variant="success"
              size="sm"
              onClick={runSequential}
              disabled={!canStart}
            >
              {running ? (
                <><Spinner animation="border" size="sm" className="me-2" />Génération en cours…</>
              ) : (
                `Lancer (${selectedForGeneration.length})`
              )}
            </Button>
          </div>
        </div>
        <div className="section-body">
          {selectedForGeneration.length === 0 ? (
            <RsEmptyHint title="File de génération vide">
              Sélectionnez des employés avec salaire sur la période, puis cliquez sur « Ajouter à la file ».
            </RsEmptyHint>
          ) : (
            <div className="salaires-table-card">
              <div className="table-section-header">
                <h6>{selectedForGeneration.length} employé(s) en attente</h6>
              </div>
              <DataTable
                columns={[
                  { label: "Contrat", render: (opt) => `#${opt.value}` },
                  { label: "Employé", render: (opt) => <span className="col-crg">{opt.label}</span> },
                  {
                    label: "Net (période)",
                    render: (opt) => (
                      <span className="gen-net-cell">{formatGnf(opt.net)}</span>
                    ),
                  },
                  {
                    label: "Statut",
                    render: (opt) => {
                      const status = statuses[opt.value];
                      const map = {
                        ok: { bg: "success", text: "Terminé" },
                        exists: { bg: "info", text: "Déjà verrouillé" },
                        error: { bg: "danger", text: "Erreur" },
                        pending: { bg: "warning", text: "En cours…" },
                      };
                      const s = map[status] || { bg: "secondary", text: "En attente" };
                      return <Badge bg={s.bg}>{s.text}</Badge>;
                    },
                  },
                  {
                    label: "",
                    render: (opt) => (
                      <Button
                        size="sm"
                        className="gen-btn-remove btn-rs-outline"
                        onClick={() => removeFromGeneration(opt.value)}
                        disabled={running}
                      >
                        Retirer
                      </Button>
                    ),
                  },
                ]}
                data={selectedForGeneration}
                emptyText="File vide"
                pageSize={25}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const SalairesPage = () => {
  const [activeTab, setActiveTab] = useState("validation");
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  const { token, role } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Vérifie l'accès
    if (!token) {
      navigate("/login");
    } else if (role !== "RH" && role !== "Assistant" && role !== "ASSISTANT" && role !== "ADMIN") {
      setUnauthorized(true);
    }
    setLoading(false);
  }, [token, role, navigate]);

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content">
        <Navbar />
        <Container fluid className="p-4 p-lg-5 salaires-page-content">
          <AppBreadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Salaires" }]} />
          <div className="salaires-page-header">
            <h4>Gestion des Salaires</h4>
            <p className="salaires-page-subtitle">
              Saisie, recherche, duplication et génération des bulletins de paie
            </p>
          </div>

          <div className="info-banner p-3 mb-4 d-flex align-items-start gap-3">
            <div className="mt-1"><FaInfoCircle /></div>
            <div className="flex-grow-1">
              <div className="fw-semibold">Rappels rapides</div>
              <ul className="mb-0 mt-1">
                <li>La recherche s'effectue quand Employé + Année + Mois sont sélectionnés.</li>
                <li>Les bulletins générés apparaissent dans l’onglet dédié et sont téléchargeables.</li>
              </ul>
            </div>
          </div>

          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
              <p className="mt-2">Chargement en cours...</p>
            </div>
          ) : unauthorized ? (
            <Alert variant="danger" className="text-center">
              ⛔ Accès refusé : seuls les utilisateurs RH ou Assistant peuvent consulter cette page.
            </Alert>
          ) : (
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
              <div className="tabs-surface salaires-tabs-wrap mb-4">
              <Nav variant="pills" className="nav-pills-modern flex-nowrap">
                <Nav.Item>
                  <Nav.Link eventKey="validation"><FaPen className="me-2" />Saisie de salaire</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="recherche"><FaSearch className="me-2" />Recherche Salaire</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="recherche-multiple"><FaSearch className="me-2" />Recherche salaire multiple (inséré)</Nav.Link>
                </Nav.Item>
                {(role === "RH" || role === "ADMIN") && (
                  <Nav.Item>
                    <Nav.Link eventKey="bulletins"><FaFilePdf className="me-2" />Bulletins générés</Nav.Link>
                  </Nav.Item>
                )}
                {role === "RH" && (
                  <Nav.Item>
                    <Nav.Link eventKey="generation-multiple"><FaLayerGroup className="me-2" />Génération multiple</Nav.Link>
                  </Nav.Item>
                )}
              </Nav>
              </div>

              <Tab.Content className="salaires-tab-content">
                <Tab.Pane eventKey="validation">
                  <ValidationSalaire />
                </Tab.Pane>
                <Tab.Pane eventKey="recherche">
                  <RechercheSalaire />
                </Tab.Pane>
                <Tab.Pane eventKey="recherche-multiple">
                  <RechercheMultipleInsereTab />
                </Tab.Pane>
                {(role === "RH" || role === "ADMIN") && (
                  <Tab.Pane eventKey="bulletins">
                    <BulletinsGeneresTab />
                  </Tab.Pane>
                )}
                {role === "RH" && (
                  <Tab.Pane eventKey="generation-multiple">
                    <GenerationMultipleTab />
                  </Tab.Pane>
                )}
              </Tab.Content>
            </Tab.Container>
          )}
        </Container>
      </div>
    </div>
  );
};

export default SalairesPage;
